<template>
  <pn-toast
    v-if="error"
    closable
    temporary
    :text="error"
    appearance="error"
    class="notification"
    @close="mailOutStore.clearError()"
  ></pn-toast>
  <div :class="[{ spinner: isLoading }]">
    <pn-spinner size="4" v-show="isLoading" />
  </div>
  <h1>Mail Out Orders</h1>
  <section>
    <div class="search-controls">
      <div class="search-item search-item-wide">
        <pn-search-field
          name="query"
          :value="searchTerm"
          placeholder="Search by Order ID or Cart ID"
          @search="onSearch"
          @update="clearSearch"
        ></pn-search-field>
      </div>
      <div class="search-item search-item-wide">
        <pn-search-field
          name="email"
          :value="emailSearchTerm"
          placeholder="Search by Email ID"
          @search="onEmailSearch"
          @update="clearEmailSearch"
        ></pn-search-field>
      </div>
      <div class="search-item search-item-status">
        <pn-select
          :value="selectedStatus"
          label="Filter by status"
          @change="onStatusChange"
        >
          <option value="" :selected="selectedStatus === ''">All</option>
          <option
            v-for="status in statusOptions"
            :key="status"
            :value="status"
            :selected="status === selectedStatus"
          >
            {{ status }}
          </option>
        </pn-select>
      </div>
    </div>
  </section>
  <section>
    <pn-table bordered="true">
      <table style="width: 100%">
        <thead>
          <tr>
            <th>Order ID</th>
            <th>Product</th>
            <th>Address Type</th>
            <th>Order Date</th>
            <th>Delivery Date</th>
            <th>Status</th>
            <th>Status Details</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="orders.length === 0 && !isLoading">
            <td colspan="7" class="text-center">No records found</td>
          </tr>
          <tr
            v-for="order in orders"
            :key="order.orderId"
            class="row-clickable"
            :class="{
              'row-urgent': order.warningClassName === 'urgent',
              'row-warning': order.warningClassName === 'warning',
            }"
            @click="router.push('/mailout/' + order.orderId)"
          >
            <td>
              <a
                class="order-id-link"
                @click.stop="router.push('/mailout/' + order.orderId)"
                >{{ order.orderId }}</a
              >
            </td>
            <td>{{ order.name }}</td>
            <td>{{ order.addressType }}</td>
            <td>{{ utc2local(order.orderDate) }}</td>
            <td>{{ utc2local(order.deliveryDate) }}</td>
            <td>{{ order.status }}</td>
            <td>{{ getStatusDetails(order.status) }}</td>
          </tr>
        </tbody>
      </table>
    </pn-table>
  </section>
  <section class="pagination-row" v-if="orders.length > 0">
    <pn-pagination
      :pages="totalPages"
      :page="currentPage"
      @pageSelected="onPageChange"
    ></pn-pagination>
  </section>
</template>

<script setup lang="ts">
import { onMounted } from "vue";
import { useRouter } from "vue-router";
import { storeToRefs } from "pinia";
import { useMailOutStore } from "@/stores/mailout";
import { utc2local } from "@/utils";
import constant from "@/constant";

const { MAILOUT_STATUSES, CREATE_MAILOUT } = constant;

const router = useRouter();
const mailOutStore = useMailOutStore();
const {
  orders,
  isLoading,
  error,
  selectedStatus,
  currentPage,
  totalPages,
  searchTerm,
  emailSearchTerm,
} = storeToRefs(mailOutStore);

const statusOptions = Object.values(MAILOUT_STATUSES);

const getStatusDetails = (status: string) =>
  CREATE_MAILOUT.STATUS_DETAILS[
    status as keyof typeof CREATE_MAILOUT.STATUS_DETAILS
  ] ?? "";

const onSearch = async (e: any) => {
  const key = (e.detail ?? "") as string;
  await mailOutStore.updateSearch(key);
};

const clearSearch = async (e: any) => {
  const key = (e.detail ?? "") as string;
  if (key) return;
  await mailOutStore.updateSearch("");
};

const onEmailSearch = async (e: any) => {
  const email = (e.detail ?? "") as string;
  await mailOutStore.updateEmailSearch(email);
};

const clearEmailSearch = async (e: any) => {
  const email = (e.detail ?? "") as string;
  if (email) return;
  await mailOutStore.updateEmailSearch("");
};

const onStatusChange = async (e: { target: { value: string } }) => {
  await mailOutStore.updateStatusFilter(e.target.value);
};

const onPageChange = async (e: any) => {
  e?.detail?.mouse?.preventDefault?.();
  const nextPage = Number(
    e?.detail?.page ?? e?.detail?.currentPage ?? e?.target?.value ?? 1,
  );
  if (
    !Number.isFinite(nextPage) ||
    nextPage < 1 ||
    nextPage === currentPage.value
  ) {
    return;
  }
  await mailOutStore.fetchOrders(nextPage);
};

onMounted(() => {
  mailOutStore.fetchOrders();
});
</script>

<style lang="scss" scoped>
.search-controls {
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  align-items: flex-end;
  flex-wrap: wrap;
}

.search-item {
  flex: 1 1 280px;
}

.search-item-wide {
  min-width: 280px;
}

.search-item-status {
  min-width: 220px;
  max-width: 320px;
}

.search-item-wide pn-search-field,
.search-item-status pn-select {
  width: 100%;
}

@media (max-width: 1100px) {
  .search-controls {
    flex-direction: column;
    align-items: stretch;
  }

  .search-item-wide,
  .search-item-status {
    width: 100%;
    min-width: 0;
    max-width: none;
  }
}

.pagination-row {
  margin-top: 1rem;
  display: flex;
  justify-content: flex-end;
}

.row-urgent td {
  background-color: #f8d7da;
}

.row-warning td {
  background-color: #fff3cd;
}

.row-clickable {
  cursor: pointer;
}

.row-clickable:hover td {
  background-color: #f0f8ff;
}

.order-id-link {
  color: #0057a8;
  text-decoration: underline;
  cursor: pointer;
}

.notification {
  position: fixed;
  bottom: 12px;
  padding: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
}

.spinner {
  width: 100%;
  height: 100%;
  position: fixed;
  left: 0;
  top: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 99;
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
